import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from '@remix-run/react'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { getErrorMessage } from '#app/utils/misc.tsx'
import { Button } from './ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => JSX.Element | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p className="mt-1 text-sm text-muted-foreground">
			{error.status}{' '}
			{typeof error.data === 'string' ? error.data : error.statusText}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = error => (
		<p className="mt-1 text-sm text-muted-foreground">
			{getErrorMessage(error)}
		</p>
	),
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => JSX.Element | null
}) {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)
	const params = useParams()

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	let status = 500
	let title = 'Unexpected Error'
	let message: JSX.Element | null = null

	if (isRouteErrorResponse(error)) {
		status = error.status
		title = `${error.status} Error`
		if (error.status === 404) {
			title = 'Page Not Found'
			message = (
				<p className="mt-1 text-sm text-muted-foreground">
					We couldn't find the page you were looking for.
				</p>
			)
		} else if (error.status === 502 || error.status === 503) {
			title = 'Server Temporarily Offline'
			message = (
				<p className="mt-1 text-sm text-muted-foreground">
					The server is currently updating or restarting. Please try again in a
					few seconds.
				</p>
			)
		} else {
			message = (statusHandlers?.[error.status] ?? defaultStatusHandler)({
				error,
				params,
			})
		}
	} else {
		message = unexpectedErrorHandler(error)
	}

	return (
		<div className="flex min-h-[50dvh] w-full items-center justify-center p-4">
			<Card className="w-full max-w-md border-red-100 bg-card shadow-lg">
				<CardHeader className="flex flex-col items-center gap-2 pb-2 text-center">
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
						<AlertTriangle className="h-6 w-6" />
					</div>
					<CardTitle className="text-xl font-bold tracking-tight text-foreground">
						{title}
					</CardTitle>
					<div className="mt-2">{message}</div>
				</CardHeader>
				<CardContent className="flex justify-center py-2">
					<p className="select-all rounded bg-muted px-2.5 py-1 font-mono text-[10px] text-muted-foreground">
						Code: {status}
					</p>
				</CardContent>
				<CardFooter className="flex justify-center pt-2">
					<Button
						onClick={() => window.location.reload()}
						className="gap-2 bg-emerald-600 font-bold text-white hover:bg-emerald-700"
					>
						<RefreshCw className="h-4 w-4" />
						Reload Page
					</Button>
				</CardFooter>
			</Card>
		</div>
	)
}
